<template>
    <print-layout>
        <div class="print-container">
            <div v-for="(load, index) in loads" :key="'load-' + index">
                <div class="text-4xl">
                    <span class="text-green"><b>PRODUCE</b></span>
                    <span class="ml-2 text-red"><b>EXPRESS</b></span>
                </div>

                <div class="pt-6">
                    <table class="w-full text-left whitespace-nowrap">
                        <tbody>
                            <tr>
                                <td class="w-min align-top">
                                    <div class="pb-2 text-left text-2xl font-bold">
                                        Load Info
                                    </div>
                                    <div><span class="inline-block w-32 font-bold">Date:</span>{{ formatDate(load.date) }}</div>
                                    <div v-if="load.type == 'delivery'">
                                        <div><span class="inline-block w-32 font-bold">Type:</span>{{ load.route_type_def }}</div>
                                        <div><span class="inline-block w-32 font-bold">Route:</span>{{ load.route }}</div>
                                        <div><span class="inline-block w-32 font-bold">Truck:</span>{{ load.truck }}</div>
                                        <div><span class="inline-block w-32 font-bold">Driver:</span>{{ load.driver }}</div>
                                        <div><span class="inline-block w-32 font-bold">Departure:</span>{{ formatTime(load.departure_time) }}</div>
                                    </div>
                                    <div v-else-if="load.type == 'pickup'">
                                        <div><span class="inline-block w-32 font-bold">Location:</span>{{ load.location_name }}</div>
                                        <div><span class="inline-block w-32 font-bold">Time:</span>{{ load.shipping_time_label }}</div>
                                    </div>
                                </td>
                                <td class="flex justify-around align-top">
                                    <div>
                                        <div class="text-xs text-center font-bold mb-1 mt-6">Milk Crate</div>
                                        <img :src="load.milk_crate_qr" alt="Milk Crate QR" class="qr-icon">
                                    </div>
                                    <div>
                                        <div class="text-xs text-center font-bold mb-1 mt-6">Update Cust. Notes</div>
                                        <img src="/images/qr_codes/update_customer_notes.png" alt="Update Customer Notes" class="qr-icon">
                                    </div>
                                    <div>
                                        <div class="text-xs text-center font-bold mb-1 mt-6">Product Issues or Returns</div>
                                        <img src="/images/qr_codes/product_return.png" alt="Product Issues or Returns" class="qr-icon">
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <table class="w-full">
                    <tbody class="striped">
                        <tr class="not_striped">
                            <th colspan="3" class="pt-6 pb-2 text-left text-2xl font-bold">
                                Visits
                            </th>
                        </tr>
                        <tr class="not_striped border-b border-gray">
                            <th class="py-1 text-right pr-4"></th>
                            <th class="py-1 text-left">Visit</th>
                            <th class="py-1 text-left">Order #</th>
                            <th class="py-1 text-center">Packaging</th>
                            <th class="py-1 text-left w-1/3">Notes</th>
                        </tr>
                        <template v-for="(load_order, loadOrderIndex) in load.load_orders_delivery" :key="'load-order-' + loadOrderIndex">
                            <tr class="border-b border-gray">
                                <td class="text-right pr-4">{{ load_order.delivery_order_index + 1 }}</td>
                                <template v-if="load_order.order_id">
                                    <td class="text-left">{{ visitDisplayName(load_order) }}</td>
                                    <td class="text-left">{{ load_order.order.order_number }}</td>
                                    <td class="text-center whitespace-nowrap">
                                        <div class="flex gap-x-2 no-wrap">
                                            <span v-if="load_order.order.box_count > 0"><font-awesome-icon :icon="['fas', 'box']" class="mr-1" /> {{ load_order.order.box_count }}</span>
                                            <span v-if="load_order.order.pallet_count > 0"><font-awesome-icon :icon="['fas', 'pallet']" class="mr-1" /> {{ load_order.order.pallet_count }}</span>
                                            <span v-if="load_order.order.tote_count > 0"><font-awesome-icon :icon="['fas', 'snowflake']" class="mr-1" /> {{ load_order.order.tote_count }}</span>
                                        </div>
                                    </td>

                                    <td v-if="load_order.order.is_wholesale" class="text-left text-xs w-1/3">{{ load_order.order.customer?.load_notes || '' }}</td>
                                    <td v-else class="text-left text-xs w-1/3">{{ load_order.order.notes || '' }}</td>
                                </template>
                                <template v-else>
                                    <td colspan="4">{{ load_order.description || '' }}</td>
                                </template>
                            </tr>
                        </template>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td></td>
                            <td colspan="4" class="text-right whitespace-nowrap">
                                <div class="inline-flex gap-x-4">
                                    <span><font-awesome-icon :icon="['fas', 'box']" class="mr-1" /> {{ getTotalBoxCount(load) }}</span>
                                    <span><font-awesome-icon :icon="['fas', 'pallet']" class="mr-1" /> {{ getTotalPalletCount(load) }}</span>
                                    <span><font-awesome-icon :icon="['fas', 'snowflake']" class="mr-1" /> {{ getTotalToteCount(load) }}</span>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td></td>
                            <td v-if="load.loader" colspan="4" class="text-right">Loaded with care by {{ load.loader.name }}</td>
                            <td v-else colspan="4"></td>
                        </tr>
                    </tfoot>
                </table>

                <div class="page-break"></div>
            </div>

            <template v-for="(order, index) in orders" :key="'order-' + index">
                <heading-info
                    :order="order"
                    :type="'confirmation'"
                />

                <order-list
                    :order="order"
                    :type="'confirmation'"
                />

                <div v-if="order.is_wholesale && order.customer && order.customer.show_printed_order_pricing" class="text-xs mt-2">
                    Prices subject to change from time of order to final invoice.
                </div>

                <div class="text-sm mt-6" :class="{'mt-4': order.is_wholesale && order.customer && order.customer.show_printed_order_pricing}">
                    Thank you for your order!
                    Please note all credits must be reported to sales@fresherproduce.ca within <b>the same day</b> of receiving your order.
                    Our customer care team will arrange pick up, and all credits will be issued once it arrives back to our warehouse upon inspection.
                </div>

                <div class="page-break"></div>
            </template>
        </div>
    </print-layout>
</template>

<script>
import PrintLayout from '@/Layouts/PrintLayout.vue';
import HeadingInfo from '@/Pages/Orders/Prints/Partials/HeadingInfo.vue';
import OrderList from '@/Pages/Orders/Prints/Partials/OrderList.vue';
import { DateTime } from "luxon";

export default {
    components: {
        PrintLayout,
        HeadingInfo,
        OrderList,
    },

    props: ['loads', 'orders', 'hst'],

    methods: {
        visitDisplayName(load_order) {
            const order = load_order?.order ?? load_order;
            if (!order) {
                return '';
            }

            const customer = order.customer;
            const fromCustomer = customer?.name?.trim() || customer?.contact_name?.trim();
            if (fromCustomer) {
                return fromCustomer;
            }

            const parts = [order.shipping_first_name, order.shipping_last_name].filter(
                (part) => part != null && String(part).trim() !== ''
            );

            return parts.join(' ');
        },

        getOrderPackagingTotal(load, key) {
            return load.load_orders_delivery.reduce((total, loadOrder) => {
                if (!loadOrder.order_id || !loadOrder.order) {
                    return total;
                }

                return total + (Number(loadOrder.order[key]) || 0);
            }, 0);
        },

        getTotalBoxCount(load) {
            return this.getOrderPackagingTotal(load, 'box_count');
        },

        getTotalPalletCount(load) {
            return this.getOrderPackagingTotal(load, 'pallet_count');
        },

        getTotalToteCount(load) {
            return this.getOrderPackagingTotal(load, 'tote_count');
        },

        formatDate(date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },
    },

    mounted() {
        if (route().params.download === undefined && (this.loads.length || this.orders.length)) {
            window.print();

            window.onafterprint = function() {
                window.close();
            };
            window.onfocus = function() {
                window.close();
            };
        }
    }
}

</script>

<style>
body {
    font-size: 90%;
    height: 100%;
}

table {
    width: 100%;
}

table td {
    -webkit-print-color-adjust: exact;
}

.combo_with_integrated_subitems {
    background-color: #EEEEEE;
    color: #515558!important;
}

.page-break {
    page-break-after: always;
}

.subheader th {
    background: #cbcbcb;
    -webkit-print-color-adjust: exact;
}

.signature {
    @apply border-r-0 border-l-0 border-t-0 border-b border-gray-dark !important;
}

.footer {
    position: fixed;
    bottom: 0px;
    left: 0px;
    width: 100%;
}

.qr-icon {
    width: 120px;
    height: 120px;
}

@media screen {
    .print-container {
        display: none;
    }
}

.striped tr:nth-child(even):not(.not_striped) {
    background-color: #f2f2f2;
}
</style>
